import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  LinearProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useLocation } from 'react-router-dom';
import { PlaceAutocompleteTextField, PlaceResult } from '../../features/location/PlaceAutocompleteTextField';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mkcx4gafu6gbomo3buhjkn7lpa0pxqqw.lambda-url.us-east-1.on.aws';

const getAuthToken = () => localStorage.getItem('token') || '';

/**
 * LocationDisclosure — Step 1: Quick post with essentials only.
 *
 * Collects:  address · location type · one primary photo · accessible / gender-neutral · notes
 * Defers to the success page (SpotLocationDisclosure):
 *   gallery · video · phone · website · hours · table type · weight limit
 */
interface IncomingState {
  place?: PlaceResult;
  locationName?: string;
}

const LocationDisclosure: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const incomingState = location.state as IncomingState | null;

  // ── Auth ───────────────────────────────────────────────────────────
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  React.useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login', { replace: true }); return; }
    setIsLoggedIn(true);
    setAuthChecked(true);
  }, [navigate]);

  // ── Pre-fill from Home page if place was selected there ────────────
  React.useEffect(() => {
    if (incomingState?.place) {
      setSelectedPlace(incomingState.place);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Form state ─────────────────────────────────────────────────────
  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>(
    incomingState?.place || null
  );
  const [locationType, setLocationType] = useState('public_facility');
  const [isAccessible, setIsAccessible] = useState(true);
  const [isGenderNeutral, setIsGenderNeutral] = useState(true);
  const [notes, setNotes] = useState('');

  // ── Primary photo ──────────────────────────────────────────────────
  const [primaryFile, setPrimaryFile] = useState<File | null>(null);
  const [primaryPreview, setPrimaryPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    if (file.size > 10 * 1024 * 1024) return;
    setPrimaryFile(file);
    setPrimaryPreview(URL.createObjectURL(file));
  };

  const removePrimaryPhoto = () => {
    setPrimaryFile(null);
    if (primaryPreview) URL.revokeObjectURL(primaryPreview);
    setPrimaryPreview(null);
  };

  // ── Submit ─────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [progress, setProgress] = useState(0); // 0-100

  const handleSubmit = async () => {
    if (!selectedPlace || !selectedPlace.lat || !selectedPlace.lng) {
      setError('Please select a valid location with coordinates');
      return;
    }
    setSubmitting(true);
    setError(null);
    setProgress(10);

    try {
      const token = getAuthToken();

      // 1) Create location
      setProgress(20);
      const locRes = await fetch(`${API_BASE_URL}/locations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          name: selectedPlace.name || selectedPlace.address,
          address: selectedPlace.address,
          latitude: selectedPlace.lat,
          longitude: selectedPlace.lng,
          type: locationType,
        }),
      });
      if (!locRes.ok) {
        const d = await locRes.json().catch(() => ({}));
        throw new Error(d.error || 'Failed to create location');
      }
      const locData = await locRes.json();
      if (!locData.id) throw new Error('No location ID returned');
      setProgress(45);

      // 2) Create station (bare-bones)
      const stationPayload: Record<string, any> = {
        locationId: locData.id,
        isAccessible,
        isGenderNeutral,
      };
      if (notes.trim()) stationPayload.notes = notes.trim();

      const stRes = await fetch(`${API_BASE_URL}/stations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(stationPayload),
      });
      if (!stRes.ok) {
        const d = await stRes.json().catch(() => ({}));
        throw new Error(d.error || 'Failed to create station');
      }
      const stData = await stRes.json();
      if (!stData.id) throw new Error('No station ID returned');
      setProgress(65);

      // 3) Upload primary photo (if any — failure is non-blocking)
      let primaryImageUrl: string | null = null;
      if (primaryFile) {
        try {
          setProgress(75);
          // Get presigned URL
          const urlRes = await fetch(`${API_BASE_URL}/photos/upload-url`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ stationId: stData.id, contentType: primaryFile.type, category: 'primary' }),
          });
          if (!urlRes.ok) throw new Error('Failed to get upload URL');
          const { uploadUrl, s3Key, finalUrl, photoId } = await urlRes.json();

          // PUT to S3
          setProgress(85);
          const putRes = await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': primaryFile.type },
            body: primaryFile,
          });
          if (!putRes.ok) throw new Error('S3 upload failed');

          // Confirm
          setProgress(90);
          const confirmRes = await fetch(`${API_BASE_URL}/photos/confirm`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ s3Key, photoId, stationId: stData.id }),
          });
          if (confirmRes.ok) {
            const cd = await confirmRes.json();
            primaryImageUrl = cd.imageUrl || finalUrl;

            // Update station imageUrl
            await fetch(`${API_BASE_URL}/stations/${stData.id}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ imageUrl: primaryImageUrl }),
            }).catch(() => {});
          }
        } catch (photoErr) {
          console.warn('[PhotoUpload] Non-blocking error:', photoErr);
          // Don't fail the whole submission
        }
      }
      setProgress(100);
      setSuccess(true);

      // Navigate to enrichment page
      setTimeout(() => {
        navigate('/spot-submitted', {
          state: {
            stationId: stData.id,
            locationId: locData.id,
            address: selectedPlace.address,
            name: selectedPlace.name || selectedPlace.address,
            lat: selectedPlace.lat,
            lng: selectedPlace.lng,
            locationType,
            primaryImageUrl,
            isAccessible,
            isGenderNeutral,
            notes: notes.trim() || undefined,
          },
        });
      }, 800);

    } catch (err: any) {
      console.error('Submit error:', err);
      setError(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (!authChecked) return null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAFA' }}>
      {/* ─── App Bar ──────────────────────────────────────────── */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: '#E94057' }}>
        <Toolbar>
          <IconButton
            edge="start"
            aria-label="back"
            onClick={() => navigate('/home')}
            data-testid="back-button"
            sx={{ mr: 2, color: '#fff' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Avatar sx={{ bgcolor: 'transparent', width: 32, height: 32, mr: 1 }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C8.5 2 5.5 4.5 5.5 8c0 1.5.5 2.9 1.4 4l-2.9 7h3l1-2.5h6l1 2.5h3l-2.9-7c.9-1.1 1.4-2.5 1.4-4 0-3.5-3-6-6.5-6zm-3 12.5l1-2.5h4l1 2.5H9zm3-5.5c-1.7 0-3-1.3-3-3s1.3-3 3-3 3 1.3 3 3-1.3 3-3 3z"/>
            </svg>
          </Avatar>
          <Typography
            variant="h6"
            data-testid="app-title"
            sx={{ flexGrow: 1, fontWeight: 700, color: '#fff', fontFamily: '"Poppins", sans-serif' }}
          >
            Post a Location
          </Typography>
          {isLoggedIn && (
            <Button
              color="inherit"
              onClick={() => { localStorage.removeItem('token'); setIsLoggedIn(false); navigate('/login'); }}
              sx={{ color: '#fff' }}
            >
              Logout
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Progress bar at the top while submitting */}
      {submitting && (
        <LinearProgress variant="determinate" value={progress} sx={{ '& .MuiLinearProgress-bar': { bgcolor: '#E94057' } }} />
      )}

      <Container maxWidth="sm" sx={{ py: 4 }}>
        {/* Header */}
        <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700, color: '#333' }}>
          📍 Add a Station
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#888' }}>
          Just the basics — you can add photos, gallery &amp; details on the next page.
        </Typography>

        {/* Error / Success */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            ✅ Station created! Taking you to the details page…
          </Alert>
        )}

        {/* ─── 1. Address ─────────────────────────────────────── */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, mb: 2.5, border: '1px solid #E0E0E0' }}>
          {/* Selected location header */}
          {selectedPlace ? (
            <Box sx={{ mb: 2 }}>
              <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                <Box sx={{ flex: 1, mr: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#E94057', lineHeight: 1.3 }}>
                    {selectedPlace.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', mt: 0.3 }}>
                    {selectedPlace.address}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setSelectedPlace(null)}
                  sx={{
                    mt: 0.25,
                    bgcolor: '#f5f5f5',
                    '&:hover': { bgcolor: '#eee' },
                  }}
                  aria-label="Clear location"
                >
                  <CloseIcon fontSize="small" sx={{ color: '#999' }} />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
              Search for the location
            </Typography>
          )}

          {/* Search bar — greyed out when a place is selected, active when cleared */}
          <Box sx={{ opacity: selectedPlace ? 0.45 : 1, pointerEvents: selectedPlace ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
            <PlaceAutocompleteTextField
              onPlaceSelect={(p: PlaceResult) => { setSelectedPlace(p); setError(null); }}
              placeholder="Enter address or place name…"
            />
          </Box>

          {selectedPlace && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#aaa' }}>
              Wrong place? Tap the ✕ above to search again.
            </Typography>
          )}
        </Paper>

        {/* ─── 2. Type + Quick Options ────────────────────────── */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, mb: 2.5, border: '1px solid #E0E0E0' }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            What kind of place is this?
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="location-type-label">Location Type</InputLabel>
            <Select
              labelId="location-type-label"
              value={locationType}
              label="Location Type"
              onChange={(e) => setLocationType(e.target.value)}
              data-testid="location-type-select"
            >
              <MenuItem value="public_facility">Public Facility</MenuItem>
              <MenuItem value="mall">Shopping Mall</MenuItem>
              <MenuItem value="restaurant">Restaurant</MenuItem>
              <MenuItem value="park">Park</MenuItem>
              <MenuItem value="hospital">Hospital / Medical</MenuItem>
              <MenuItem value="airport">Airport</MenuItem>
              <MenuItem value="gas_station">Gas Station</MenuItem>
              <MenuItem value="store">Retail Store</MenuItem>
              <MenuItem value="library">Library</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Chip
              label="♿ Accessible"
              variant={isAccessible ? 'filled' : 'outlined'}
              color={isAccessible ? 'success' : 'default'}
              onClick={() => setIsAccessible(!isAccessible)}
              sx={{ fontWeight: 600 }}
            />
            <Chip
              label="🚻 Gender Neutral"
              variant={isGenderNeutral ? 'filled' : 'outlined'}
              color={isGenderNeutral ? 'info' : 'default'}
              onClick={() => setIsGenderNeutral(!isGenderNeutral)}
              sx={{ fontWeight: 600 }}
            />
          </Stack>
        </Paper>

        {/* ─── 3. Primary Photo (optional) ────────────────────── */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, mb: 2.5, border: '1px solid #E0E0E0' }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            📸 Primary Photo <Typography component="span" variant="caption" sx={{ color: '#aaa' }}>(optional)</Typography>
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mb: 2, color: '#888' }}>
            One quick snap — add more on the next page.
          </Typography>

          {!primaryPreview ? (
            <Button
              variant="outlined"
              component="label"
              startIcon={<PhotoCameraIcon />}
              disabled={submitting || success}
              sx={{
                borderColor: '#E94057',
                color: '#E94057',
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': { borderColor: '#d63649', bgcolor: 'rgba(233,64,87,.04)' },
              }}
            >
              Choose Photo
              <input
                type="file"
                accept="image/*"
                hidden
                ref={fileInputRef}
                onChange={handlePhotoSelect}
                data-testid="photo-upload-input"
              />
            </Button>
          ) : (
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <img
                src={primaryPreview}
                alt="Primary preview"
                style={{
                  width: '100%',
                  maxHeight: 200,
                  objectFit: 'cover',
                  borderRadius: 12,
                  border: '1px solid #E0E0E0',
                }}
              />
              <IconButton
                size="small"
                onClick={removePrimaryPhoto}
                sx={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  bgcolor: 'rgba(0,0,0,.55)',
                  color: '#fff',
                  '&:hover': { bgcolor: 'rgba(0,0,0,.75)' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Paper>

        {/* ─── 4. Notes (optional) ────────────────────────────── */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, mb: 3, border: '1px solid #E0E0E0' }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            📝 Quick Note <Typography component="span" variant="caption" sx={{ color: '#aaa' }}>(optional)</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="e.g., 2nd floor near food court, very clean…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            inputProps={{ maxLength: 300 }}
            helperText={`${notes.length}/300`}
            data-testid="notes-input"
            size="small"
          />
        </Paper>

        {/* ─── Submit ─────────────────────────────────────────── */}
        <Button
          variant="contained"
          fullWidth
          onClick={handleSubmit}
          disabled={submitting || !selectedPlace || success}
          data-testid="submit-button"
          sx={{
            bgcolor: '#E94057',
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 600,
            py: 1.5,
            fontSize: '1.1rem',
            '&:hover': { bgcolor: '#d63649' },
            '&:disabled': { bgcolor: '#ccc' },
          }}
        >
          {submitting ? (
            <>
              <CircularProgress size={20} sx={{ color: '#fff', mr: 1 }} />
              Creating…
            </>
          ) : success ? (
            '✅ Created!'
          ) : (
            'Create Station →'
          )}
        </Button>

        <Typography variant="caption" sx={{ display: 'block', mt: 1.5, textAlign: 'center', color: '#aaa' }}>
          You'll be able to add gallery, video, hours &amp; more on the next page.
        </Typography>

        {/* Powered by Google */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <img
            src="https://developers.google.com/static/maps/documentation/images/powered_by_google_on_white.png"
            alt="Powered by Google"
            style={{ height: 18, opacity: 0.7 }}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default LocationDisclosure;
